<template>
    <div id="new-zoho-channel-form">
        <a-form>
            <div class="form-wrapper">
                <div class="input-wrapper d-none">
                    <label>{{ $t("whatsappChannel.channelName") }}</label>
                    <a-form-item
                        :validate-status="getErrorStatus('channelName')"
                        :help="getErrorMessage('channelName')"
                    >
                        <a-input
                            v-model="form.channelName"
                            :placeholder="$t('whatsappChannel.channelName')"
                            name="channelName"
                            ref="channelName"
                        />
                    </a-form-item>
                </div>
                <div class="input-wrapper">
                    <label>Zoho Account URL</label>
                    <div class="zoho-account-urls">
                        <div
                            v-for="url in zohoAccountUrls"
                            :key="url.value"
                            class="zoho-account-url"
                            :class="{ selected: form.accountUrl === url.value }"
                            @click="form.accountUrl = url.value"
                        >
                            <span class="region">{{ url.label }}</span>
                            <span class="url">{{ url.value }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </a-form>

        <div class="form-footer">
            <div class="actions">
                <a-button @click="handleCancel">{{ $t("message.cancel") }}</a-button>
                <a-button class="pr-confirm-btn" @click="handleSubmit" type="primary" :disabled="loading">
                    {{ $t("mail.connect") }}
                </a-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import CONFIG from "../../../../config/config";
import { copyText2Clipboard } from "../../../../utils/utils";
import { socketMessageStream } from "../../../../utils/socketStream";
import { first, map, timeout } from "rxjs/operators";

export default {
    name: "CreateZohoChannel",
    props: {
        channel: {
            type: Object,
            default: () => {},
        },
    },
    components: {},
    data() {
        return {
            form: {
                channelName: "Zoho",
                accountUrl: "https://accounts.zoho.com",
            },
            zohoAccountUrls: [
                { label: "US", value: "https://accounts.zoho.com" },
                { label: "AU", value: "https://accounts.zoho.com.au" },
                { label: "EU", value: "https://accounts.zoho.eu" },
                { label: "IN", value: "https://accounts.zoho.in" },
                { label: "CN", value: "https://accounts.zoho.com.cn" },
                { label: "JP", value: "https://accounts.zoho.jp" },
                { label: "SA (Saudi Arabia)", value: "https://accounts.zoho.sa" },
                { label: "CA (Canada)", value: "https://accounts.zohocloud.ca" },
            ],
            loading: false,
            clone: _.cloneDeep(this.channel),
            submitClicked: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site?._id,
        }),
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
        }),
        getErrorStatus(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? "error" : "";
        },
        getErrorMessage(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? this.$t("message.requiredChannel") : "";
        },
        handleCancel() {
            this.$emit("cancel");
        },
        getRedirectURL() {
            return `${CONFIG.SOCKET_URL}/webhook/oauth2/zoho`;
        },
        startOauth() {
            this.loading = true;
            const redirectUrl = this.getRedirectURL();
            const state = `${this.siteId}_${redirectUrl}`;
            const CLIENT_ID = "1000.N2HT8IWMPR9WREGR8WUDIHOVAZ843A";

            const scopes = [
                "ZohoCRM.modules.ALL",
                "ZohoCRM.settings.ALL",
                "ZohoCRM.users.ALL",
                "ZohoCRM.org.ALL",
                "ZohoCRM.bulk.ALL",
                "ZohoCRM.notifications.ALL",
                "ZohoCRM.Files.CREATE",
                "ZohoCRM.Files.READ",
                "ZohoCRM.change_owner.CREATE",
            ];

            const url = `${this.form.accountUrl}/oauth/v2/auth?scope=${scopes.join(
                ","
            )}&client_id=${CLIENT_ID}&response_type=code&access_type=offline&redirect_uri=${redirectUrl}&state=${state}`;
            this.listenSocket();
            this.window = window.open(url, "_blank");
        },
        listenSocket() {
            socketMessageStream
                .pipe(
                    first((x) => x.cmd === "zoho_callback_response"),
                    map((x) => x.data),
                    timeout(30000)
                )
                .toPromise()
                .then(async (data) => {
                    const payload = data;
                    if (!data?.success) {
                        this.loading = false;
                        this.window.close();
                        this.$message.error("Failed to connect Zoho CRM");
                        return;
                    }
                    await this.completeCreateChannel(payload);
                    this.loading = false;
                    this.window.close();
                    this.$message.success("Zoho CRM connected successfully");
                })
                .catch((err) => {
                    this.loading = false;
                    this.window.close();
                    this.$message.error("Failed to connect Zoho CRM");
                });
        },
        completeCreateChannel(payload) {
            const data = {
                payload,
                type: "zoho",
                name: "Zoho",
            };
            return this.createChannel(data)
                .then(() => {
                    this.$message.success("Channel created successfully");
                    this.$emit("ok");
                })
                .catch((err) => {
                    this.$message.error("Failed to create channel");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleSubmit() {
            this.submitClicked = true;
            if (this.form.channelName.length < 1) {
                this.$refs.channelName.focus();
                return;
            } else {
                const isValid = true;
                if (isValid) {
                    this.startOauth();
                }
            }
        },
    },
    mounted() {},
    beforeDestroy() {},
};
</script>

<style lang="scss" scoped>
#new-zoho-channel-form {
    min-height: 250px;
    height: 600px;
    flex-direction: column;

    .ant-form {
        height: 470px;
        max-height: unset !important;
    }

    .form-wrapper {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .webhook-info {
            display: flex;
            gap: 5px;
            justify-content: center;
            padding: 5px;
            overflow-wrap: anywhere;

            .anticon {
                &:hover {
                    color: var(--color-text-info-hover);
                }
            }
        }

        .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 5px;
            height: 75px;
        }
    }

    .form-footer {
        display: flex;
        justify-content: flex-end;
        padding: 10px;
        margin-top: 20px;
        background: var(--color-bg-default);
        border-top: 1px solid var(--color-border-default);
    }
}

.zoho-account-urls {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .zoho-account-url {
        padding: 10px;
        border: 1px solid var(--color-border-default);
        border-radius: 4px;
        cursor: pointer;

        &.selected {
            border-color: var(--color-avatar-blue-500);
        }

        .region {
            font-weight: bold;
        }

        .url {
            font-size: 12px;
            color: var(--color-text-secondary);
        }
    }
}
</style>

<style lang="scss">
.new-sms-postacell {
    .custom-modal-body {
        padding: 0px !important;
    }
}
</style>
